// Derechos ARCO del titular comercial (LOPDP) — el equivalente de
// medical/DerechosArco.tsx para un cliente o prospecto del CRM.
//
// Existe porque en cuanto el CRM escribe por WhatsApp, el titular de los datos
// deja de ser sólo un paciente: es alguien cuyo celular, conversaciones y
// adjuntos están en la base y que tiene derecho a verlos y a que se borren.
// RPCs del script 843.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Modal, fieldCls } from '@/features/inventario/shared';
import { Campo } from '@/features/admin/shared';
import { fechaLocal } from '@/lib/utils/fecha';

const CRM = { headers: { 'Content-Profile': 'crm', 'Accept-Profile': 'crm' } };

function descargarJSON(data: unknown, nombre: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

interface Resultado {
  modo_aplicado: string; huella_fiscal: number; motivo_de_conservar: string | null;
  mensajes_eliminados: number; conversaciones_eliminadas: number; numeros_suprimidos: number;
}

export function ArcoTitular({ partnerId, nombre, onClose, onHecho }: {
  partnerId: string; nombre: string; onClose: () => void; onHecho?: () => void;
}) {
  const [modo, setModo] = useState<'anonimizar' | 'eliminar'>('anonimizar');
  const [motivo, setMotivo] = useState('');
  const [confirmo, setConfirmo] = useState(false);
  const [hecho, setHecho] = useState<Resultado | null>(null);

  const exportar = useMutation({
    mutationFn: async () =>
      (await apiClient.post('/rpc/crm_exportar_datos_partner', { _partner: partnerId }, CRM)).data,
    onSuccess: (data) => {
      descargarJSON(data, `datos-personales-${nombre.replace(/\s+/g, '_').toLowerCase()}-${fechaLocal()}.json`);
      toast.success('Datos exportados');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const eliminar = useMutation({
    mutationFn: async () => (await apiClient.post('/rpc/crm_eliminar_datos_partner',
      { _partner: partnerId, _modo: modo, _motivo: motivo }, CRM)).data as Resultado,
    onSuccess: (r) => { setHecho(r); onHecho?.(); toast.success('Solicitud aplicada'); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title={`Datos personales de ${nombre}`} onClose={onClose}>
      {hecho ? (
        <div className="space-y-3 text-sm">
          <p className="font-medium">
            Se aplicó: <span className="capitalize">{hecho.modo_aplicado}</span>
          </p>
          <ul className="list-disc pl-5 text-muted-foreground space-y-1">
            <li>{hecho.mensajes_eliminados} mensaje(s) y {hecho.conversaciones_eliminadas} conversación(es) eliminadas.</li>
            <li>{hecho.numeros_suprimidos} número(s) añadidos a la lista de supresión: no se les volverá a escribir.</li>
          </ul>
          {hecho.motivo_de_conservar && (
            <p className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              {hecho.motivo_de_conservar}
            </p>
          )}
          <div className="text-right"><Button size="sm" onClick={onClose}>Cerrar</Button></div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium mb-1">Acceso y portabilidad</p>
            <p className="text-muted-foreground mb-2">
              Entrega en JSON todo lo que la empresa guarda de esta persona: su ficha, de dónde
              salió el dato, quién lo recibió, los consentimientos y el hilo de WhatsApp.
            </p>
            <Button size="sm" variant="outline" isLoading={exportar.isPending}
                    onClick={() => exportar.mutate()}>
              <Download className="w-4 h-4 mr-1" /> Exportar datos
            </Button>
          </div>

          <div className="border-t pt-4">
            <p className="font-medium mb-1 flex items-center gap-1 text-red-600">
              <ShieldAlert className="w-4 h-4" /> Eliminación
            </p>
            <p className="text-muted-foreground mb-3">
              El número queda en la lista de supresión aunque se borre todo lo demás: es la única
              forma de garantizar que no se le vuelva a escribir. Si la persona tiene facturas o
              asientos contables, esos documentos se conservan por obligación tributaria y sólo se
              anonimiza el contacto.
            </p>
            <div className="space-y-2 mb-3">
              <label className="flex items-start gap-2">
                <input type="radio" className="mt-1" checked={modo === 'anonimizar'}
                       onChange={() => setModo('anonimizar')} />
                <span><b>Anonimizar</b> — se borra el contacto y se conserva la identificación.</span>
              </label>
              <label className="flex items-start gap-2">
                <input type="radio" className="mt-1" checked={modo === 'eliminar'}
                       onChange={() => setModo('eliminar')} />
                <span><b>Eliminar</b> — además se sustituye la identificación por un seudónimo irreversible.</span>
              </label>
            </div>
            <Campo label="Motivo (solicitud ARCO, expediente…)">
              <input className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder="Solicitud ARCO recibida el …" />
            </Campo>
            <label className="flex items-center gap-2 mt-3">
              <input type="checkbox" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} />
              Confirmo que deseo {modo === 'eliminar' ? 'eliminar' : 'anonimizar'} estos datos.
            </label>
            <div className="text-right mt-3">
              <Button size="sm" variant="outline" className="text-red-600"
                      disabled={!confirmo || motivo.trim().length < 5}
                      isLoading={eliminar.isPending} onClick={() => eliminar.mutate()}>
                <Trash2 className="w-4 h-4 mr-1" /> Aplicar
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
